import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertOctagon, RotateCw, Home } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { sendTelemetry } from '@/lib/telemetry';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional name used to scope the report (e.g. 'Overview', 'AppShell'). */
  scope?: string;
  /** Custom fallback factory; defaults to a tasteful full-page card. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * App-wide error boundary.
 *
 *   - Catches render-phase exceptions in any descendant component tree.
 *   - Reports to `/api/bi/telemetry/` via `sendTelemetry` (best-effort).
 *   - Renders a calm fallback with Reset / Reload / Home actions.
 *
 * Use one near the router root, plus optional smaller boundaries around
 * heavy widgets (charts, command palette) to scope failures.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // We don't await — telemetry is fire-and-forget.
    void sendTelemetry({
      kind: 'render_error',
      message: error.message,
      stack: error.stack,
      component: this.props.scope ?? 'unknown',
      extra: { component_stack: info.componentStack ?? '' },
    });
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.error('[ErrorBoundary]', this.props.scope ?? '', error, info);
    }
  }

  reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(error, this.reset);
    return <DefaultFallback error={error} onReset={this.reset} />;
  }
}

function DefaultFallback({ error, onReset }: { error: Error; onReset: () => void }) {
  return (
    <div className="flex min-h-[60vh] items-center justify-center px-6 py-12">
      <div className="relative w-full max-w-md overflow-hidden rounded-xl border border-border bg-surface p-8 text-center shadow-soft">
        <div className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-danger/10 text-danger">
          <AlertOctagon className="h-6 w-6" aria-hidden />
        </div>
        <h2 className="font-display text-xl font-bold tracking-tight">Well, that wasn't supposed to happen.</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The dashboard crashed while rendering. We've logged the report; you can try again or head home.
        </p>
        {import.meta.env.DEV ? (
          <pre className="mt-4 max-h-40 overflow-auto rounded-md bg-muted px-3 py-2 text-left text-[11px] text-muted-foreground">
            {error.message}
            {error.stack ? `\n\n${error.stack}` : ''}
          </pre>
        ) : null}
        <div className="mt-6 flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" onClick={onReset}>
            <RotateCw className="h-3.5 w-3.5" aria-hidden /> Try again
          </Button>
          <Button size="sm" onClick={() => (window.location.href = '/')}>
            <Home className="h-3.5 w-3.5" aria-hidden /> Take me home
          </Button>
        </div>
      </div>
    </div>
  );
}
